import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Confetti from 'react-confetti';
import { 
  LinkIcon,
  SparklesIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  UserGroupIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { fetchUserStats } from '../../utils/analytics';
import { asAbortSignal, withAbortSignal } from '../../utils/abortSignal';
import { mapDualProgramLinks } from '../../utils/referralLinks';
import RewardCardVisual from '../shared/RewardCardVisual';
import toast from 'react-hot-toast';
import { useNotifications } from '../../hooks/useNotifications';
import { usePendingGiftsMenuBadge } from '../../hooks/usePendingGiftsMenuBadge';
import DashboardLayout from '../ui/DashboardLayout';
import BackToTopButton from '../ui/BackToTopButton';
import AnalyticsDashboard from './AnalyticsDashboard';
import ClientHomeView from '../enrola/ClientHomeView';
import ClientWalletView from '../enrola/ClientWalletView';
import ClientClubView from '../enrola/ClientClubView';
import ClientPromotionsView from '../enrola/ClientPromotionsView';
import PromotionsIntroModal from '../enrola/PromotionsIntroModal';
import ClientEnterpriseHub from '../enrola/ClientEnterpriseHub';
import VideoHub from '../videos/VideoHub';
import ClientGiftsInTransitView from '../enrola/ClientGiftsInTransitView';
import ClientContactsView from '../enrola/ClientContactsView';
import ProgramReferralCard from '../enrola/ProgramReferralCard';
import ReferralTierStepper from '../enrola/ReferralTierStepper';
import NextTierBenefitCard from '../enrola/NextTierBenefitCard';
import AnimatedStatNumber from '../enrola/AnimatedStatNumber';
import InviteeStatusTimeline from '../enrola/InviteeStatusTimeline';
import ReferralInviteEmptyState from '../enrola/ReferralInviteEmptyState';
import ClientWelcomeOnboarding from '../enrola/ClientWelcomeOnboarding';
import NotificationsCenter from './NotificationsCenter';
import SettingsPanel from './SettingsPanel';
import { FullPageSkeleton, ListPageSkeleton, RewardsViewSkeleton } from '../ui/PageSkeleton';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import PageHeader from '../ui/PageHeader';
import TableToolbar from '../ui/TableToolbar';
import { navigateFromNotificationAction, CLIENT_NOTIFICATION_VIEWS } from '../../utils/notifications';
import { ELEVATE_HISPANO_SLUG } from '../../utils/elevateEvent';

function ClientDashboard() {
  const { user, logout, refreshUser } = useAuth();
  const { t, formatMessage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadCount, updateCount } = useNotifications();
  const isElevateHispano = user?.company_slug === ELEVATE_HISPANO_SLUG;
  const { count: giftsPendingCount, refreshCount: refreshGiftsPendingCount } = usePendingGiftsMenuBadge(
    isElevateHispano ? null : 'client'
  );
  const [referralLinks, setReferralLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('dashboard');
  const [showNotifications, setShowNotifications] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // Start collapsed on mobile
  const [searchValue, setSearchValue] = useState('');
  
  // Network data state
  const [networkData, setNetworkData] = useState([]);
  const [networkLoading, setNetworkLoading] = useState(true);
  
  // Rewards data state
  const [rewardsSummary, setRewardsSummary] = useState(null);
  const [availableRewards, setAvailableRewards] = useState([]);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [rewardsLoading, setRewardsLoading] = useState(true);
  const [claimingRewardId, setClaimingRewardId] = useState(null);
  const [actionOutcome, setActionOutcome] = useState(null);
  const [shortActions, setShortActions] = useState([]);
  const [shortActionsLoading, setShortActionsLoading] = useState(true);
  const [rewardConfetti, setRewardConfetti] = useState(false);
  const [pointsHistorySearch, setPointsHistorySearch] = useState('');
  const [pointsHistoryStateFilter, setPointsHistoryStateFilter] = useState('all');
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  const [promotionsBadge, setPromotionsBadge] = useState(0);
  const [showPromotionsIntro, setShowPromotionsIntro] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: typeof window !== 'undefined' ? window.innerWidth : 0, height: typeof window !== 'undefined' ? window.innerHeight : 0 });

  const welcomeTierRewards = useMemo(
    () => availableRewards.filter((reward) => reward.is_welcome_choice),
    [availableRewards]
  );
  const catalogRewards = useMemo(
    () => availableRewards.filter((reward) => !reward.is_welcome_choice),
    [availableRewards]
  );

  const currentPoints = useMemo(
    () => rewardsSummary?.current_points ?? userStats?.rewardPoints ?? 0,
    [rewardsSummary?.current_points, userStats?.rewardPoints]
  );

  const handlePointsBalanceUpdate = useCallback((points) => {
    setRewardsSummary((prev) => ({ ...(prev || {}), current_points: points }));
    refreshUser();
  }, [refreshUser]);

  const fireRewardConfetti = useCallback(() => {
    setRewardConfetti(true);
    setTimeout(() => setRewardConfetti(false), 4500);
  }, []);

  // Welcome onboarding after signup bonus (Elevate: all club clients until discount redeemed).
  const shouldShowWelcomeOnboarding =
    user?.is_client &&
    !user?.welcome_onboarding_completed &&
    !welcomeDismissed &&
    (
      isElevateHispano ||
      (user?.welcome_bonus_received || 0) > 0
    );

  const handleWelcomeOnboardingComplete = useCallback(async () => {
    setWelcomeDismissed(true);
    await refreshUser();
  }, [refreshUser]);

  const handleWelcomeRedeem = useCallback((reward) => {
    const isClubDiscount = reward?.fulfillment_mode === 'club_discount';
    setActiveView(isClubDiscount && isElevateHispano ? 'club' : 'rewards');
    fireRewardConfetti();
  }, [fireRewardConfetti, isElevateHispano]);

  const fetchPromotionsSummary = useCallback(async () => {
    if (!isElevateHispano) {
      setPromotionsBadge(0);
      setShowPromotionsIntro(false);
      return;
    }
    try {
      const response = await axios.get('/api/client/promotions/summary', { withCredentials: true });
      setPromotionsBadge(response.data?.unseen_count || 0);
      setShowPromotionsIntro(Boolean(response.data?.show_intro));
    } catch (error) {
      if (error?.response?.status !== 401) {
        console.error('Error fetching promotions summary:', error);
      }
      setPromotionsBadge(0);
      setShowPromotionsIntro(false);
    }
  }, [isElevateHispano]);

  const handlePromotionsSeen = useCallback(() => {
    setPromotionsBadge(0);
  }, []);

  const handlePromotionsIntroView = useCallback(() => {
    setShowPromotionsIntro(false);
    setActiveView('promotions');
  }, []);

  const handlePromotionsIntroClose = useCallback(() => {
    setShowPromotionsIntro(false);
  }, []);

  useEffect(() => {
    fetchPromotionsSummary();
  }, [fetchPromotionsSummary]);

  useEffect(() => {
    if (activeView === 'promotions') {
      setPromotionsBadge(0);
    }
  }, [activeView]);

  useEffect(() => {
    const onResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchReferralLinks = useCallback(async (signal) => {
    try {
      const response = await axios.get(
        '/api/referral-links',
        withAbortSignal({ withCredentials: true }, signal),
      );
      setReferralLinks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (
        error?.name === 'CanceledError'
        || error?.code === 'ERR_CANCELED'
        || error?.response?.status === 401
      ) {
        return;
      }
      console.error('Error fetching referral links:', error);
      toast.error(t.dashboard.errorLoadingReferralLinks);
    } finally {
      setLoading(false);
    }
  }, [t.dashboard.errorLoadingReferralLinks]);

  const fetchAnalyticsData = useCallback(async (signal) => {
    try {
      const statsResponse = await fetchUserStats({ signal: asAbortSignal(signal) });
      setUserStats(statsResponse);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Error fetching analytics data:', error);
      toast.error(t.dashboard.failedToLoadAnalytics);
    }
  }, [t.dashboard.failedToLoadAnalytics]);

  useEffect(() => {
    const controller = new AbortController();
    fetchReferralLinks(controller.signal);
    fetchAnalyticsData(controller.signal);
    return () => controller.abort();
  }, [fetchReferralLinks, fetchAnalyticsData]);

  useEffect(() => {
    if (activeView === 'network') {
      setActiveView('referrals');
    }
    if (isElevateHispano && activeView === 'giftsInTransit') {
      setActiveView('dashboard');
    }
    if (!isElevateHispano && activeView === 'club') {
      setActiveView('dashboard');
    }
    if (!isElevateHispano && activeView === 'promotions') {
      setActiveView('dashboard');
    }
  }, [activeView, isElevateHispano]);

  useEffect(() => {
    const view = new URLSearchParams(location.search).get('view');
    if (view === 'network') {
      setActiveView('referrals');
      return;
    }
    if (view && CLIENT_NOTIFICATION_VIEWS.has(view)) {
      if (view === 'giftsInTransit' && isElevateHispano) {
        setActiveView('dashboard');
        return;
      }
      if (view === 'club' && !isElevateHispano) {
        setActiveView('dashboard');
        return;
      }
      setActiveView(view);
    }
  }, [location.search, isElevateHispano]);

  // Fetch network data
  useEffect(() => {
    const fetchNetworkData = async () => {
      try {
        const response = await axios.get('/api/network', {
          withCredentials: true
        });
        setNetworkData(response.data);
      } catch (error) {
        console.error('Error fetching network data:', error);
        setNetworkData([]);
        toast.error(t.dashboard.failedToLoadNetwork);
      } finally {
        setNetworkLoading(false);
      }
    };

    if (activeView === 'referrals' || activeView === 'dashboard') {
      fetchNetworkData();
    }
  }, [activeView, t.dashboard.failedToLoadNetwork]);

  const fetchShortActions = useCallback(async () => {
    try {
      const response = await axios.get('/api/client/short-actions', { withCredentials: true });
      const actions = response.data.actions || [];
      setShortActions(actions);
    } catch (error) {
      console.error('Error fetching short actions:', error);
      setShortActions([]);
    } finally {
      setShortActionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeView === 'dashboard') {
      setShortActionsLoading(true);
      fetchShortActions();
    }
  }, [activeView, fetchShortActions]);

  useEffect(() => {
    let cancelled = false;

    const fetchPointsSummary = async () => {
      try {
        const response = await axios.get('/api/client/rewards/summary', { withCredentials: true });
        if (!cancelled) {
          setRewardsSummary(response.data);
        }
      } catch (error) {
        console.error('Error fetching points summary:', error);
      }
    };

    fetchPointsSummary();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch rewards data
  useEffect(() => {
    const fetchRewardsData = async () => {
      setRewardsLoading(true);
      try {
        const [summaryResponse, rewardsResponse, historyResponse] = await Promise.all([
          axios.get('/api/client/rewards/summary', { withCredentials: true }),
          axios.get('/api/client/rewards/available', { withCredentials: true }),
          axios.get('/api/client/rewards/points-history', { withCredentials: true })
        ]);
        
        setRewardsSummary(summaryResponse.data);
        setAvailableRewards(rewardsResponse.data);
        setPointsHistory(historyResponse.data);
      } catch (error) {
        console.error('Error fetching rewards data:', error);
        toast.error(t.dashboard.failedToLoadRewards);
      } finally {
        setRewardsLoading(false);
      }
    };

    if (activeView === 'rewards' || activeView === 'dashboard') {
      fetchRewardsData();
    }
  }, [activeView, t.dashboard.failedToLoadRewards]);

  const handleClaimReward = async (rewardId) => {
    setClaimingRewardId(rewardId);
    try {
      const response = await axios.post(
        `/api/client/rewards/${rewardId}/claim`,
        {},
        { withCredentials: true }
      );
      const claimedReward = availableRewards.find((reward) => reward.id === rewardId);
      const isClubDiscount =
        claimedReward?.fulfillment_mode === 'club_discount'
        || response.data?.fulfillment_mode === 'club_discount';
      fireRewardConfetti();
      toast.success(t.dashboard.rewardClaimed);
      const outcomeCopy = t.ux?.actionOutcome?.rewardClaimed;
      setActionOutcome({
        id: `claim-${rewardId}-${Date.now()}`,
        type: 'rewardClaimed',
        title: outcomeCopy?.title || t.dashboard.rewardClaimed,
        description: claimedReward?.name
          ? `${outcomeCopy?.description || ''} (${claimedReward.name})`
          : outcomeCopy?.description,
        nextStep: isClubDiscount
          ? (t.enrola?.clubView?.welcomeDiscountNextStep || outcomeCopy?.nextStep)
          : outcomeCopy?.nextStep,
      });
      setRewardsSummary((prev) =>
        prev ? { ...prev, current_points: response.data.reward_points } : prev
      );
      setAvailableRewards((prev) =>
        prev.map((reward) =>
          reward.id === rewardId
            ? response.data?.activation_only
              ? {
                  ...reward,
                  claim_status: 'activated',
                }
              : {
                  ...reward,
                  can_redeem: false,
                  has_active_claim: true,
                  claim_status: isClubDiscount ? 'completed' : 'pending_admin',
                }
            : reward
        )
      );
      await refreshUser();
      fetchAnalyticsData();
      if (isClubDiscount && isElevateHispano) {
        setActiveView('club');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t.dashboard.claimFailed);
    } finally {
      setClaimingRewardId(null);
    }
  };


  const mapDualPrograms = mapDualProgramLinks;

  const linksList = Array.isArray(referralLinks) ? referralLinks : [];

  // Calculate stats
  const totalClicks = linksList.reduce((sum, link) => sum + (link.clicks || 0), 0);
  const totalConversions = linksList.reduce((sum, link) => sum + (link.conversions || 0), 0);
  const activeLinks = linksList.filter((link) => link.is_active !== false).length;

  const handleNotificationClick = () => {
    setShowNotifications(true);
  };

  const handleNotificationNavigate = useCallback((actionPath) => {
    navigateFromNotificationAction(navigate, actionPath, {
      onViewChange: (view) => {
        if (CLIENT_NOTIFICATION_VIEWS.has(view)) {
          setActiveView(view);
        }
      },
    });
  }, [navigate]);

  if (loading) {
    return <FullPageSkeleton />;
  }

  const getPageTitle = () => {
    const d = t.dashboard;
    switch (activeView) {
      case 'analytics':
        return d.analytics;
      case 'referrals':
        return t.enrola.programs;
      case 'rewards':
        return d.rewards;
      case 'contacts':
        return t.myContacts.title;
      case 'giftsInTransit':
        return t.giftsInTransit.title;
      case 'wallet':
        return t.enrola.wallet;
      case 'club':
        return t.enrola.club;
      case 'promotions':
        return t.promotions?.title || 'Promotions';
      case 'corporate':
        return t.enrola.corporate;
      case 'videoHub':
        return t.videoHub.title;
      case 'settings':
        return d.settings;
      default:
        return t.enrola.giftsHome;
    }
  };

  const getPageSubtitle = () => {
    const d = t.dashboard;
    switch (activeView) {
      case 'analytics':
        return d.trackPerformance;
      case 'referrals':
        return t.enrola.programsSubtitle;
      case 'rewards':
        return d.viewRewardPoints;
      case 'contacts':
        return t.myContacts.subtitle;
      case 'giftsInTransit':
        return t.giftsInTransit.subtitle;
      case 'wallet':
        return t.enrola.walletBtn;
      case 'club':
        return isElevateHispano
          ? (t.enrola?.clubView?.elevateSubtitle || t.enrola.shoppingClubBtn)
          : t.enrola.shoppingClubBtn;
      case 'promotions':
        return t.promotions?.subtitle;
      case 'corporate':
        return t.enrola.corporate;
      case 'videoHub':
        return t.videoHub.clientSubtitle;
      case 'settings':
        return d.customizeAccount;
      default:
        return t.enrola.clientHome?.homeSubtitle || t.enrola.giftsSubtitle;
    }
  };

  // Component to render different views
  const renderContent = () => {
    switch (activeView) {
      case 'analytics':
        return <AnalyticsDashboard />;
      case 'settings':
        return <SettingsPanel />;
      case 'referrals':
        return renderReferralsView();
      case 'rewards':
        return renderRewardsView();
      case 'contacts':
        return <ClientContactsView />;
      case 'giftsInTransit':
        return <ClientGiftsInTransitView onPendingCountChange={refreshGiftsPendingCount} />;
      case 'wallet':
        return <ClientWalletView />;
      case 'club':
        return <ClientClubView />;
      case 'promotions':
        return (
          <ClientPromotionsView
            onOpenReferrals={() => setActiveView('referrals')}
            onSeen={handlePromotionsSeen}
          />
        );
      case 'corporate':
        return <ClientEnterpriseHub />;
      case 'videoHub':
        return <VideoHub audience="client" />;
      default:
        return renderDashboard();
    }
  };

  const renderDashboard = () => (
    <>
      {rewardConfetti && (
        <div className="pointer-events-none fixed inset-0 z-[200]">
          <Confetti width={windowSize.width} height={windowSize.height} recycle={false} numberOfPieces={320} gravity={0.22} />
        </div>
      )}
      <ClientHomeView
        username={user?.username}
        points={currentPoints}
        rewardsSummary={rewardsSummary}
        availableRewards={availableRewards}
        rewardsLoading={rewardsLoading}
        referralLinks={linksList}
        networkMembers={networkData}
        networkLoading={networkLoading}
        claimingRewardId={claimingRewardId}
        onClaimReward={handleClaimReward}
        onOpenRewards={() => setActiveView('rewards')}
        onOpenReferrals={() => setActiveView('referrals')}
        onOpenGiftsInTransit={() => setActiveView('giftsInTransit')}
        actionOutcome={actionOutcome}
        onDismissActionOutcome={() => setActionOutcome(null)}
        shortActions={shortActions}
        shortActionsLoading={shortActionsLoading}
        onRefreshShortActions={fetchShortActions}
        onShortActionPointsEarned={handlePointsBalanceUpdate}
        hideRewardsShop={false}
        hideGiftsInTransit={isElevateHispano}
      />
    </>
  );

  const renderReferralsView = () => {
    const { clientsLink, partnersLink } = mapDualPrograms(linksList);
    return (
      <div className="space-y-6 xl:space-y-7">
        <PageHeader
          eyebrow="Enrola"
          icon={LinkIcon}
          title={t.enrola?.programs || 'Referral programs'}
          subtitle={t.enrola.programsSubtitle}
        />

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <ReferralTierStepper totalReferrals={totalConversions} />
          </div>
          <div className="lg:col-span-4 flex">
            <NextTierBenefitCard totalReferrals={totalConversions} className="w-full" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="metric-tile lg:col-span-4"
          >
            <p className="stat-label">{t.enrola.clicks}</p>
            <p className="metric-tile-value text-secondary-900 ">
              <AnimatedStatNumber value={totalClicks} />
            </p>
            <p className="mt-2 text-sm text-secondary-600 ">{t.enrola.clicksAllLinks}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="metric-tile lg:col-span-4"
          >
            <p className="stat-label">{t.enrola.conversionsLabel}</p>
            <p className="metric-tile-value text-orange-600 ">
              <AnimatedStatNumber value={totalConversions} />
            </p>
            <p className="mt-2 text-sm text-secondary-600 ">{t.enrola.conversionsViaLink}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="metric-tile metric-tile-accent lg:col-span-4"
          >
            <p className="stat-label text-amber-800/90 ">Enlaces activos</p>
            <p className="metric-tile-value text-secondary-900 ">
              <AnimatedStatNumber value={activeLinks} />
            </p>
            <p className="mt-2 text-sm text-secondary-600 ">Programas disponibles</p>
          </motion.div>

          <div className="lg:col-span-6">
            <ProgramReferralCard programKey="clients" link={clientsLink} />
          </div>
          <div className="lg:col-span-6">
            <ProgramReferralCard programKey="partners" link={partnersLink} />
          </div>
        </div>

        {renderNetworkSection()}
      </div>
    );
  };

  const renderNetworkSection = () => (
    <section className="space-y-5 border-t border-secondary-200/80 pt-6 xl:space-y-6">
      <div>
        <p className="eyebrow">{t.enrola.network.eyebrow}</p>
        <h3 className="mt-1 text-xl font-bold tracking-tight text-secondary-900  sm:text-2xl">
          {t.enrola.network.title}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-secondary-600 ">
          {networkData.length
            ? t.enrola.network.subtitleTimeline
            : t.enrola.network.subtitle}
        </p>
      </div>

      {networkLoading ? (
        <ListPageSkeleton rows={5} showHeader={false} />
      ) : !networkData.length ? (
        <ReferralInviteEmptyState />
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3 xl:gap-6">
          <div className="card-premium p-5 xl:p-6">
            <h4 className="text-lg font-semibold text-secondary-900  mb-4">{t.enrola.network.summary}</h4>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-secondary-600 ">{t.enrola.totalReferralsLabel}</span>
                <span className="font-semibold tabular-nums">
                  <AnimatedStatNumber value={networkData.length} />
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-600 ">{t.enrola.network.active}</span>
                <span className="font-semibold tabular-nums">
                  <AnimatedStatNumber value={networkData.filter(n => n.status === 'active').length} />
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-600 ">{t.enrola.network.referralsGenerated}</span>
                <span className="font-semibold tabular-nums">
                  <AnimatedStatNumber value={networkData.reduce((sum, n) => sum + (n.referrals || 0), 0)} />
                </span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="card-premium overflow-hidden">
              <div className="p-6 border-b border-white/20">
                <h4 className="text-lg font-semibold text-secondary-900 ">{t.enrola.network.members}</h4>
                <p className="text-sm text-secondary-500  mt-1">{t.enrola.network.timelineHint}</p>
              </div>
              <div className="divide-y divide-white/20">
                {networkData.map((member, index) => (
                  <div key={`member-${member.id}-${index}`} className="p-4 hover:bg-secondary-50/30 transition-colors">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className="w-10 h-10 shrink-0 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {member.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-secondary-900  truncate">{member.name}</p>
                          <p className="text-sm text-secondary-500  truncate">{member.email}</p>
                        </div>
                      </div>
                      <div className="text-left sm:text-right shrink-0">
                        <p className="text-sm font-medium text-secondary-900 ">{formatMessage(t.enrola.referralsCount, { count: member.referrals })}</p>
                        <p className="text-xs text-secondary-500 ">{t.dashboard.joined} {new Date(member.joined).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="mt-3 sm:pl-12">
                      <InviteeStatusTimeline member={member} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );

  const renderRewardsView = () => {
    if (rewardsLoading) {
      return <RewardsViewSkeleton />;
    }

    const d = t.dashboard;
    const tb = t.common.table;
    const normalizedPointsSearch = pointsHistorySearch.trim().toLowerCase();
    const filteredPointsHistory = pointsHistory.filter((record) => {
      if (pointsHistoryStateFilter !== 'all' && record.to_state !== pointsHistoryStateFilter) {
        return false;
      }
      if (!normalizedPointsSearch) return true;
      const haystack = [
        record.date,
        record.referred_client,
        record.from_state,
        record.to_state,
        String(record.points_awarded),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedPointsSearch);
    });
    const hasPointsHistoryFilters =
      pointsHistorySearch.trim() !== '' || pointsHistoryStateFilter !== 'all';
    const clearPointsHistoryFilters = () => {
      setPointsHistorySearch('');
      setPointsHistoryStateFilter('all');
    };
    const eventStateOptions = [
      { value: 'all', label: tb.filterAll },
      { value: 'affiliated', label: 'affiliated' },
      { value: 'contacted', label: 'contacted' },
      { value: 'visited', label: 'visited' },
      { value: 'completed', label: 'completed' },
    ];

    const renderRewardCard = (reward, index) => (
      <motion.article
        key={reward.id}
        aria-label={reward.name}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, delay: index * 0.08 }}
        className={`interactive-lift overflow-hidden rounded-2xl border-2 bg-white  transition-[border-color,box-shadow] duration-300 flex flex-col focus-within:ring-2 focus-within:ring-orange-400/40 ${
          reward.has_active_claim
            ? 'border-secondary-300  opacity-95'
            : reward.can_redeem
            ? 'border-success-400 shadow-lg shadow-success-500/15 ring-1 ring-success-300/50'
            : 'border-secondary-200  opacity-95 hover:opacity-100'
        }`}
      >
        <div className="relative aspect-[16/10] overflow-hidden bg-secondary-100 ">
          <RewardCardVisual reward={reward} />
          <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 rounded-full bg-black/50 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
            <StarIcon className="w-4 h-4 text-amber-400" />
            {reward.points_required} pts
          </div>
          {reward.can_redeem && !reward.has_active_claim && (
            <span className="absolute top-3 left-3 z-10 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-success-500 text-white shadow-sm">
              {t.dashboard.available}
            </span>
          )}
          {reward.has_active_claim && (
            <span className="absolute top-3 left-3 z-10 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-secondary-500 text-white shadow-sm">
              {t.dashboard.giftOnTheWay}
            </span>
          )}
        </div>

        <div className="p-5 flex flex-col flex-1">
          <h4 className="mb-2 font-semibold text-secondary-900 ">{reward.name}</h4>
          <p className="text-sm text-secondary-600  mb-4 flex-1">
            {reward.description || t.dashboard.noDescription}
          </p>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <StarIcon className="w-5 h-5 text-amber-500" />
              <span className="font-semibold text-secondary-900 ">
                {reward.points_required}
              </span>
              <span className="text-sm text-secondary-600 ">{t.dashboard.points}</span>
            </div>
            {!reward.can_redeem && (
              <span className="text-xs text-secondary-500 ">
                {formatMessage(t.dashboard.moreNeeded, { count: reward.points_needed })}
              </span>
            )}
          </div>

          {reward.has_active_claim ? (
            <button
              type="button"
              disabled
              className="mt-4 w-full cursor-not-allowed rounded-xl bg-secondary-200 py-2.5 text-sm font-semibold text-secondary-500  "
            >
              {t.dashboard.giftOnTheWay}
            </button>
          ) : reward.can_redeem ? (
            <Button
              type="button"
              variant="primary"
              size="small"
              className="mt-4 w-full !from-success-600 !to-emerald-600 !shadow-[0_8px_24px_rgba(22,163,74,0.28)] !ring-success-400/30 hover:!from-success-700 hover:!to-emerald-700"
              disabled={claimingRewardId === reward.id}
              loading={claimingRewardId === reward.id}
              onClick={() => handleClaimReward(reward.id)}
            >
              {claimingRewardId === reward.id ? t.dashboard.creating : t.dashboard.claimReward}
            </Button>
          ) : null}
        </div>
      </motion.article>
    );

    const renderRewardsSection = (title, subtitle, rewardsList) => (
      <div className="space-y-4">
        <div>
          <h4 className="text-lg font-bold text-secondary-900 ">{title}</h4>
          <p className="text-sm text-secondary-600 ">{subtitle}</p>
        </div>
        {rewardsList.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rewardsList.map((reward, index) => renderRewardCard(reward, index))}
          </div>
        ) : (
          <EmptyState
            icon={SparklesIcon}
            title={t.dashboard.noRewardsAvailable}
            description={t.dashboard.checkBackLater}
          />
        )}
      </div>
    );

    return (
      <>
        {rewardConfetti && (
          <div className="pointer-events-none fixed inset-0 z-[200]">
            <Confetti width={windowSize.width} height={windowSize.height} recycle={false} numberOfPieces={320} gravity={0.22} />
          </div>
        )}
      <div className="space-y-6">
        {/* Rewards — bento layout */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="md:col-span-5 card-premium p-5 xl:p-6 bg-gradient-to-br from-orange-50/50 to-amber-50/30 border border-orange-100/60 shadow-soft rounded-3xl   "
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-secondary-900 ">{t.dashboard.currentPoints}</h3>
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center shadow-glow">
                <StarIcon className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="mb-2">
              <p className="text-4xl font-bold text-orange-600 tabular-nums">
                <AnimatedStatNumber value={rewardsSummary?.current_points || 0} />
              </p>
              <p className="text-sm text-secondary-600  mt-1">
                {t.dashboard.historicalTotal}{' '}
                <span className="font-semibold text-secondary-800 tabular-nums">
                  <AnimatedStatNumber value={rewardsSummary?.total_points_earned || 0} />
                </span>{' '}
                pts
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="md:col-span-4 card-premium p-5 xl:p-6 rounded-3xl shadow-soft"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-secondary-900 ">{t.enrola.yourReferrals}</h3>
              <UserGroupIcon className="w-8 h-8 text-orange-500" />
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-secondary-600 ">{t.dashboard.totalLabel}</span>
                <span className="text-lg font-semibold text-secondary-900  tabular-nums">
                  <AnimatedStatNumber value={rewardsSummary?.total_referrals || 0} />
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-secondary-600 ">{t.dashboard.activeLabel}</span>
                <span className="text-lg font-semibold text-amber-600 tabular-nums">
                  <AnimatedStatNumber value={rewardsSummary?.active_referrals || 0} />
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-secondary-600 ">{t.dashboard.completedLabel}</span>
                <span className="text-lg font-semibold text-success-600 tabular-nums">
                  <AnimatedStatNumber value={rewardsSummary?.completed_referrals || 0} />
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="md:col-span-3 card-premium p-5 xl:p-6 bg-gradient-to-br from-success-50/50 to-emerald-50/30 border border-emerald-100/50 rounded-3xl shadow-soft"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-secondary-900 ">{t.dashboard.nextReward}</h3>
              <SparklesIcon className="w-8 h-8 text-success-500" />
            </div>
            {rewardsSummary?.next_reward ? (
              <div>
                <p className="font-semibold text-secondary-900  mb-2">
                  {rewardsSummary.next_reward.name}
                </p>
                <p className="text-sm text-secondary-600  mb-3">
                  {formatMessage(t.enrola.pointsNeeded, { count: rewardsSummary.next_reward.points_needed })}
                </p>
                <div className="w-full bg-secondary-100 rounded-full h-3 mb-2">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${rewardsSummary.next_reward.progress_percentage}%` }}
                    transition={{ duration: 1.5, delay: 0.5 }}
                    className="h-full bg-gradient-to-r from-success-500 to-emerald-500 rounded-full"
                  />
                </div>
                <p className="text-xs text-secondary-500 ">
                  {formatMessage(t.dashboard.percentComplete, { percent: rewardsSummary.next_reward.progress_percentage })}
                </p>
              </div>
            ) : (
              <p className="text-sm text-secondary-600 ">
                {t.dashboard.noMoreGoalsReached}
              </p>
            )}
          </motion.div>
        </div>

        {/* Available Rewards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="card-premium p-5 xl:p-6 space-y-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-secondary-900 ">{t.dashboard.availableRewards}</h3>
              <p className="text-sm text-secondary-600 ">
                {t.dashboard.redeemSubtitle}
              </p>
            </div>
            <CurrencyDollarIcon className="w-8 h-8 text-orange-500" />
          </div>

          {availableRewards.length > 0 ? (
            <>
              {renderRewardsSection(
                t.dashboard.welcomeRewardsTitle,
                t.dashboard.welcomeRewardsSubtitle,
                welcomeTierRewards
              )}
              {catalogRewards.length > 0 ? (
                <div className="border-t border-secondary-100 pt-6 ">
                  {renderRewardsSection(
                    t.dashboard.catalogRewardsTitle,
                    t.dashboard.catalogRewardsSubtitle,
                    catalogRewards
                  )}
                </div>
              ) : null}
            </>
          ) : (
            <EmptyState
              icon={SparklesIcon}
              title={t.dashboard.noRewardsAvailable}
              description={t.dashboard.checkBackLater}
            />
          )}
        </motion.div>

        {/* Points History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="card-premium p-5 xl:p-6 max-lg:p-4"
        >
          <div className="mb-6 flex items-center justify-between max-lg:mb-4 max-lg:flex-col max-lg:items-start max-lg:gap-3">
            <div className="min-w-0">
              <h3 className="text-xl font-bold text-secondary-900 ">{t.dashboard.pointsHistory}</h3>
              <p className="text-sm text-secondary-600 ">
                {t.dashboard.trackEarnedPoints}
              </p>
            </div>
            <ChartBarIcon className="hidden h-8 w-8 shrink-0 text-orange-500 sm:block" />
          </div>

          {pointsHistory.length > 0 ? (
            <>
              <div className="mb-4">
                <TableToolbar
                  searchValue={pointsHistorySearch}
                  onSearchChange={setPointsHistorySearch}
                  searchPlaceholder={d.searchPointsHistory}
                  filters={[
                    {
                      id: 'eventState',
                      label: d.filterByEventState,
                      value: pointsHistoryStateFilter,
                      onChange: setPointsHistoryStateFilter,
                      options: eventStateOptions,
                    },
                  ]}
                  hasActiveFilters={hasPointsHistoryFilters}
                  onClearFilters={clearPointsHistoryFilters}
                  resultCount={filteredPointsHistory.length}
                  totalCount={pointsHistory.length}
                />
              </div>

              {filteredPointsHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-secondary-200">
                    <th className="px-2 py-3 text-left text-xs font-semibold text-secondary-900  sm:px-4 sm:text-sm">
                      {t.dashboard.date}
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-semibold text-secondary-900  sm:px-4 sm:text-sm">
                      {t.dashboard.event}
                    </th>
                    <th className="hidden px-2 py-3 text-left text-sm font-semibold text-secondary-900  md:table-cell md:px-4">
                      {t.dashboard.referredClient}
                    </th>
                    <th className="px-2 py-3 text-right text-sm font-semibold text-secondary-900  sm:px-4">
                      {t.dashboard.pointsEarned}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPointsHistory.map((record, index) => (
                    <motion.tr
                      key={record.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="border-b border-secondary-100 hover:bg-secondary-50/30 transition-colors"
                    >
                      <td className="px-2 py-3 text-xs text-secondary-600  sm:px-4 sm:text-sm">
                        {record.date}
                      </td>
                      <td className="px-2 py-3 sm:px-4">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            record.to_state === 'completed' 
                              ? 'bg-success-100 text-success-700'
                              : record.to_state === 'visited'
                              ? 'bg-orange-100 text-orange-700'
                              : record.to_state === 'contacted'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-secondary-100 text-secondary-700  '
                          }`}>
                            {record.from_state} → {record.to_state}
                          </span>
                        </div>
                      </td>
                      <td className="hidden px-2 py-3 text-sm text-secondary-900  md:table-cell md:px-4">
                        {record.referred_client}
                      </td>
                      <td className="px-2 py-3 text-right sm:px-4">
                        <span className="inline-flex items-center space-x-1 text-success-600 font-semibold">
                          <span>+{record.points_awarded}</span>
                          <StarIcon className="w-4 h-4" />
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
              ) : (
                <EmptyState
                  compact
                  title={d.noMatchingPointsHistory}
                  description={tb.clearFilters}
                />
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <ChartBarIcon className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
              <p className="text-secondary-600 ">{t.dashboard.noPointsHistoryYet}</p>
              <p className="text-sm text-secondary-500  mt-2">
                {t.dashboard.startReferringClients}
              </p>
            </div>
          )}
        </motion.div>
      </div>
      </>
    );
  };

  return (
    <DashboardLayout
      activeView={activeView}
      sidebarCollapsed={sidebarCollapsed}
      setSidebarCollapsed={setSidebarCollapsed}
      sidebarProps={{
        activeView,
        setActiveView,
        onLogout: logout,
        notificationsCount: unreadCount,
        menuBadges: {
          giftsInTransit: isElevateHispano ? 0 : giftsPendingCount,
          promotions: isElevateHispano ? promotionsBadge : 0,
        },
      }}
      topBarProps={{
        title: getPageTitle(),
        subtitle: getPageSubtitle(),
        onNotificationClick: handleNotificationClick,
        notificationsCount: unreadCount,
        searchValue,
        onSearchChange: setSearchValue,
        onSettingsClick: () => setActiveView('settings'),
        pointsBalance: currentPoints,
      }}
      overlays={
        <>
          <NotificationsCenter
            isOpen={showNotifications}
            onClose={() => setShowNotifications(false)}
            onNotificationUpdate={updateCount}
            onNavigate={handleNotificationNavigate}
          />
          {shouldShowWelcomeOnboarding && (
            <ClientWelcomeOnboarding
              username={user?.username}
              onComplete={async () => {
                await handleWelcomeOnboardingComplete();
                fetchPromotionsSummary();
              }}
              onRedeem={handleWelcomeRedeem}
              onViewPromotions={handlePromotionsIntroView}
            />
          )}
          {isElevateHispano && showPromotionsIntro && !shouldShowWelcomeOnboarding && (
            <PromotionsIntroModal
              onClose={handlePromotionsIntroClose}
              onViewPromotions={handlePromotionsIntroView}
            />
          )}
          <BackToTopButton />
        </>
      }
    >
      {renderContent()}
    </DashboardLayout>
  );
}

export default ClientDashboard;
